import Vue from 'vue';
import VueRouter, {RouteConfig} from 'vue-router';
import {cloneDeep} from 'lodash';

import i18n, {availableLocales} from '@/translations';

import IndexPage from '@/pages/index.vue';

import ErrorPage from '@/pages/error.vue';

import prefix from '@/middlewares/prefix';
import absoluteRedirect from '@/middlewares/absoluteRedirect';
import preloaded from '@/middlewares/preloaded';

import Green from '@/pages/cards/Green.vue';
import White from '@/pages/cards/White.vue';
import Gold from '@/pages/cards/Gold.vue';
// import Pricing from '@/pages/pricing.vue';
import Help from '@/pages/help/help.vue';
import HelpDetail from '@/pages/help/detail.vue';
import AboutPage from '@/pages/about.vue';
import ContactPage from '@/pages/contact.vue';
import StatusPage from '@/pages/status.vue';
import DownloadPage from '@/pages/download.vue';

const middlewares = [prefix, absoluteRedirect, preloaded];

const routes = [
	{
		path: 'routes.status.path',
		component: StatusPage,
		name: 'status',
		meta: {
			// `name` property is used in HEAD application to generate
			// meta data for active route - needs to be filled for each route if
			// you want different meta (default value: `index`)
			index: false,
			layout: 'default',
			middlewares
		}
	},
	{
		path: 'routes.index.path',
		component: IndexPage,
		name: 'index',
		meta: {
			// `name` property is used in HEAD application to generate
			// meta data for active route - needs to be filled for each route if
			// you want different meta (default value: `index`)
			index: true,
			layout: 'default',
			middlewares: [prefix, absoluteRedirect]
		}
	},
	{
		path: 'routes.cards.green.path',
		component: Green,
		name: 'orkaGreen',
		meta: {
			index: true,
			headerStyle: 'green',
			type: 'green',
			layout: 'default',
			middlewares
		}
	},
	{
		path: 'routes.cards.white.path',
		component: White,
		name: 'orkaWhite',
		meta: {
			index: true,
			headerStyle: 'white',
			type: 'white',
			layout: 'default',
			middlewares
		}
	},
	{
		path: 'routes.cards.gold.path',
		component: Gold,
		name: 'orkaGold',
		meta: {
			index: true,
			headerStyle: 'gold',
			type: 'gold',
			layout: 'default',
			middlewares
		}
	},
	// {
	// 	path: 'routes.pricing.path',
	// 	component: Pricing,
	// 	name: 'pricing',
	// 	meta: {
	// 		index: true,
	// 		headerStyle: 'white-blue',
	// 		layout: 'default',
	// 		middlewares
	// 	}
	// },
	{
		path: 'routes.help.path',
		component: Help,
		name: 'help',
		meta: {
			index: true,
			layout: 'default',
			middlewares
		}
	},
	{
		path: 'routes.helpDetail.path',
		component: HelpDetail,
		name: 'helpDetail',
		meta: {
			index: false,
			layout: 'default',
			faq: true,
			middlewares
		}
	},
	//{
		// path: 'routes.about.path',
		// component: AboutPage,
		//name: 'about',
		//meta: {
	//	index: true,
	//		layout: 'default',
	//		middlewares
	//		}
	//},
	{
		path: 'routes.contact.path',
		component: ContactPage,
		name: 'contact',
		meta: {
			index: true,
			layout: 'default',
			middlewares
		}
	},
	{
		path: 'routes.download.path',
		component: DownloadPage,
		name: 'download',
		meta: {
			index: true,
			layout: 'default',
			middlewares
		}
	},
	{
		path: 'routes.getApp.path',
		component: ErrorPage,
		name: 'getApp',
		meta: {
			index: true,
			layout: 'default',
			middlewares
		}
	},
	{
		path: 'routes.tempRedirect.path',
		redirect: 'routes.index.path',
		name: 'tempRedirect',
		meta: {
			index: false,
			code: 307,
			middlewares
		}
	},
	{
		path: 'routes.permRedirect.path',
		redirect: 'routes.index.path',
		name: 'permRedirect',
		meta: {
			index: false,
			code: 301,
			middlewares
		}
	},
	{
		path: 'routes.absRedirect.path',
		name: 'absRedirect',
		meta: {
			index: false,
			absoluteRedirect: 'routes.absRedirect.redirect',
			code: 307,
			middlewares
		}
	}
];

const errorRoute = {
	path: '*',
	component: ErrorPage,
	name: 'error',
	meta: {
		name: 'error',
		layout: 'default',
		code: 404,
		middlewares,
		index: false
	}
};

const enhancedRoutes = [
	...availableLocales
		.map((availableLocale: string) => cloneDeep(routes).map((route: RouteConfig) => {
			if (route.redirect) {
				route.redirect = i18n.t(route.redirect as any, availableLocale) as string;
			}

			if (route.meta.absoluteRedirect) {
				route.meta.absoluteRedirect = i18n.t(route.meta.absoluteRedirect, availableLocale) as string;
			}

			return !route.meta.code ? {
				...route,
				name: `${route.name}-${availableLocale}`,
				path: i18n.t(route.path, availableLocale),
				meta: {
					...route.meta,
					localeName: `${route.name}.${availableLocale}`,
					code: 200
				}
			} : {
				...route,
				name: `${route.name}-${availableLocale}`,
				path: i18n.t(route.path, availableLocale),
				meta: {
					...route.meta,
					localeName: `${route.name}.${availableLocale}`,
				}
			}
		}))
		.reduce((routes: any[], route: any[]) => {
			routes = [...routes, ...route];
			return routes;
		}, []),
	errorRoute
]

Vue.use(VueRouter);

let queryBase = null;

if (typeof window !== 'undefined' && window.location.search.length) {
	const query = window.location.search
		.substr(1)
		.split('&');

	query.forEach((fullQuery) => {
		const [name, value] = fullQuery.split('=');

		if (name === 'base') {
			queryBase = value;
		}
	});
}

const router = new (VueRouter as any)({
	mode: 'history',
	base: queryBase ? queryBase : serverVariables.httpRequest.basePath,
	routes: enhancedRoutes,

	scrollBehavior(newRoute: any, oldRoute: any) {
		if (newRoute.name === oldRoute.name && newRoute.query.section) {
			return;
		}

		return {y: 0, x: 0};
	}
});

if (typeof window !== 'undefined') {
	(window as any).vueRouter = router;
}

export {
	routes,
	router
};

export default router;
